import Container from '@/components/base/container.component';
import {
  LocalShippingOutlined,
  PersonOutlined,
  PlaceOutlined,
} from '@mui/icons-material';
import { Box, ButtonBase, Stack, Typography } from '@mui/material';
import { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Link as RouterLink,
  Outlet,
  useMatch,
  useResolvedPath,
} from 'react-router-dom';

interface SettingsNavItem {
  to: string;
  label: string;
  icon: ReactNode;
}

function SettingsNavButton({ item }: { item: SettingsNavItem }) {
  const resolved = useResolvedPath(item.to);
  const active = useMatch({ path: resolved.pathname, end: true }) !== null;

  return (
    <ButtonBase
      component={RouterLink}
      to={item.to}
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-start',
        gap: 1.25,
        width: { xs: 'auto', md: '100%' },
        flexShrink: 0,
        px: 1.75,
        py: 1.25,
        borderRadius: 2,
        whiteSpace: 'nowrap',
        color: active ? 'primary.main' : 'text.secondary',
        bgcolor: active ? 'rgba(2, 52, 76, 0.06)' : 'transparent',
        border: '1px solid',
        borderColor: active ? 'rgba(2, 52, 76, 0.16)' : 'transparent',
        transition: 'all 0.15s',
        '&:hover': {
          bgcolor: active ? 'rgba(2, 52, 76, 0.09)' : 'action.hover',
        },
      }}
    >
      <Box sx={{ display: 'flex', color: 'inherit' }}>{item.icon}</Box>
      <Typography
        variant="body2"
        sx={{ fontWeight: active ? 700 : 600, fontSize: '0.85rem' }}
      >
        {item.label}
      </Typography>
    </ButtonBase>
  );
}

export function SettingsLayout() {
  const { t } = useTranslation('customer');

  const navItems: SettingsNavItem[] = [
    {
      to: 'profile',
      label: t('settings.nav.profile'),
      icon: <PersonOutlined sx={{ fontSize: 20 }} />,
    },
    {
      to: 'addresses',
      label: t('settings.nav.addresses'),
      icon: <PlaceOutlined sx={{ fontSize: 20 }} />,
    },
    {
      to: 'shipping',
      label: t('settings.nav.shipping'),
      icon: <LocalShippingOutlined sx={{ fontSize: 20 }} />,
    },
  ];

  return (
    <Container maxWidth="lg" sx={{ px: 0, width: '100%' }}>
      <Box sx={{ mb: 3 }}>
        <Typography
          variant="h4"
          sx={{
            fontWeight: 700,
            fontSize: { xs: '1.5rem', sm: '1.85rem' },
          }}
        >
          {t('settings.title')}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          {t('settings.subtitle')}
        </Typography>
      </Box>

      <Stack
        direction={{ xs: 'column', md: 'row' }}
        spacing={3}
        sx={{ alignItems: 'flex-start' }}
      >
        <Stack
          direction={{ xs: 'row', md: 'column' }}
          spacing={0.5}
          sx={{
            width: { xs: '100%', md: 232 },
            flexShrink: 0,
            position: { md: 'sticky' },
            top: { md: 88 },
            overflowX: { xs: 'auto', md: 'visible' },
            pb: { xs: 0.5, md: 0 },
          }}
        >
          {navItems.map((item) => (
            <SettingsNavButton key={item.to} item={item} />
          ))}
        </Stack>

        <Box sx={{ flex: 1, minWidth: 0, width: '100%' }}>
          <Outlet />
        </Box>
      </Stack>
    </Container>
  );
}
